import { createSelector, lruMemoize } from '@reduxjs/toolkit';
import { isNil } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import type { AssetsAssetTranscriptionPropertiesSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type {
  AllWordsIndex,
  SpeakerPart,
  Word,
} from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import { getSelectWordsIndexTree } from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/utils';

import {
  selectSelectedTranscriptionPropertiesIdByAssetId,
  selectTranscriptionSegmentsByAssetId,
} from '../../ducks/asset_transcriptions/selectors';
import { ICONS } from '../../icons/icon';
import type { AppState } from '../../store/types';
import type { MenuItem } from '../../ui/menus/menu/types';

const getCurrentAssetSegments = (
  state: AppState,
  assetId: string | undefined,
) =>
  assetId ? (selectTranscriptionSegmentsByAssetId(state, assetId) ?? []) : [];

const getTranscriptionProperties = (
  state: AppState,
  assetId: string | undefined,
): AssetsAssetTranscriptionPropertiesSchemaWithIdNullable | undefined =>
  assetId
    ? selectSelectedTranscriptionPropertiesIdByAssetId(state, assetId)
    : undefined;

export const selectTranscriptionPartsAndSpeakerLabels = createSelector(
  getCurrentAssetSegments,
  getTranscriptionProperties,
  (transcriptionSegments, transcriptionProperties) => {
    const segmentsSorted = transcriptionSegments.sort((a, b) => {
      if (a.time_start_milliseconds && b.time_start_milliseconds) {
        return a.time_start_milliseconds - b.time_start_milliseconds;
      }
      return 0;
    });

    const speakerParts: SpeakerPart[] = [];

    segmentsSorted.forEach(({ id: segmentId, transcription }) => {
      if (!segmentId || !transcription) {
        return;
      }
      const lastGroup = speakerParts[speakerParts.length - 1];
      // Create new group if groups are empty or if the speaker has changed
      if (!speakerParts.length || lastGroup.speaker !== transcription.speaker) {
        const newPart: SpeakerPart = {
          speaker: transcription.speaker,
          ids: [segmentId],
        };
        speakerParts.push(newPart);
      } else {
        lastGroup.ids.push(segmentId);
      }
    });

    const speakerLabelsFromProperties =
      transcriptionProperties?.speaker_labels || {};
    const speakerLabels: Record<string, string> = {
      ...speakerLabelsFromProperties,
    };
    speakerParts
      .map((part) => part.speaker)
      .forEach((speakerId) => {
        if (typeof speakerId === 'number' && !speakerLabels[speakerId]) {
          speakerLabels[speakerId] = `Speaker ${speakerId + 1}`;
        }
      });

    return { speakerParts, speakerLabels };
  },
);

export const getSpeakerPartBySegmentIdMap = lruMemoize(
  (speakerParts: SpeakerPart[]) => {
    const speakerMap = new Map<string, number>();
    for (const speakerPart of speakerParts) {
      for (const segmentId of speakerPart.ids) {
        if (isNil(speakerPart.speaker)) {
          continue;
        }
        speakerMap.set(segmentId, speakerPart.speaker);
      }
    }
    return speakerMap;
  },
);

export const selectSpeakerMenuItems = createCachedSelector(
  [
    (speakerLabels: Record<string, string>) => speakerLabels,
    (
      _speakerLabels: Record<string, string>,
      speakerId: number | null | undefined,
    ) => speakerId,
  ],
  (
    speakerLabels: Record<string, string>,
    currentSpeakerId: number | null | undefined,
  ) => {
    const speakersMenuItems: MenuItem[] = [];
    Object.keys(speakerLabels).forEach((speakerId) => {
      if (
        isNil(currentSpeakerId) ||
        parseInt(speakerId, 10) !== currentSpeakerId
      ) {
        speakersMenuItems.push({
          name: `speaker_${speakerId}`,
          label: speakerLabels[speakerId],
          icon: ICONS.User,
        });
      }
    });

    return [
      ...speakersMenuItems,
      { name: 'manage', label: 'Manage Speakers', icon: ICONS.Cog },
    ];
  },
)(
  (
    _speakerLabels: Record<string, string>,
    speakerId: number | null | undefined,
  ) => speakerId,
);

export const selectAllWords = createSelector(
  getCurrentAssetSegments,
  (transcriptionSegments) => {
    let allWords: Word[] = [];
    let globalIndex = -1;
    transcriptionSegments
      .filter((transcription) => transcription?.status !== 'DELETED')
      .forEach((segment, segmentIndex) => {
        if (segment && segment.transcription && segment.transcription.words) {
          allWords = allWords.concat(
            segment.transcription.words.map((word, index) => {
              globalIndex += 1;

              return {
                segmentId: segment.id,
                segmentIndex,
                localIndex: index,
                globalIndex,
                timeStart:
                  !isNil(segment.time_start_milliseconds) &&
                  word.start_ms !== undefined
                    ? segment.time_start_milliseconds + word.start_ms
                    : undefined,
                timeEnd:
                  !isNil(segment.time_start_milliseconds) &&
                  word.end_ms !== undefined
                    ? segment.time_start_milliseconds + word.end_ms
                    : undefined,
                ...word,
              };
            }),
          );
        }
      });

    const allWordsIndex: AllWordsIndex = {};
    for (let i = 0; i < allWords.length; i += 1) {
      const id = `w_${allWords[i].segmentId}_${allWords[i].localIndex}`;
      allWordsIndex[id] = i;
    }

    return { allWords, allWordsIndex };
  },
);

export const selectWordsIndexTree = createSelector(
  [selectAllWords],
  ({ allWords }) => getSelectWordsIndexTree(allWords),
);
